import { faCheck, faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap';

import { TableCell } from 'Components/Table/Table/Table';
import { Text } from 'Components/Text/Text';
import { ServiceAssessmentState } from 'Models/TPRM';

import styles from './ServiceAssessmentProgressTableCell.module.css';
import sharedStyles from '../../ThirdPartiesAndServicesListingSharedStyles.module.css';
import { ThirdPartiesAndServicesTableRowData } from '../../ThirdPartiesAndServicesTableRow/ThirdPartiesAndServicesTableRow';
import { PrimaryTextAndSecondaryTextTableCell } from '../PrimaryTextAndSecondaryTextTableCell/PrimaryTextAndSecondaryTextTableCell';

/**
 * A list of pairs of strings. The elements in each pair are used as follows:
 * * [0]: A description of one of the steps in the progress bar. Displayed in the tooltip/hover of the table cell.
 * * [1]: A description of the _next_ step in the progress bar to be completed. Displayed in the table cell.
 */
const STEPS_NON_TPRM_USER = [
    ['Third party and third-party service created', ''],
    ['Third party submitted to be finalized', 'Third party not yet submitted to be finalized'],
    ['Third-party service submitted to be finalized', 'Third-party service not yet submitted to be finalized'],
    ['Third party finalized', 'Third party pending finalization'],
    ['Third-party service finalized', 'Third-party service pending finalization'],
    ['Assessment started', 'Assessment not started'],
    ['Inherent risk questionnaire finalized', 'Inherent risk questionnaire being finalized'],
    ['Third-party questionnaire submitted by third party', 'Third-party questionnaire in progress'],
    ['Final review submitted', 'Final review in progress'],
];

/**
 * A list of pairs of strings. The elements in each pair are used as follows:
 * * [0]: A description of one of the steps in the progress bar. Displayed in the tooltip/hover of the table cell.
 * * [1]: A description of the _next_ step in the progress bar to be completed. Displayed in the table cell.
 */
const STEPS_TPRM_USER = [
    ['Third party and third-party service created', ''],
    ['Third party finalized', 'Third party pending finalization'],
    ['Third-party service finalized', 'Third-party service pending finalization'],
    ['Assessment started', 'Assessment not started'],
    ['Inherent risk questionnaire submitted', 'Inherent risk questionnaire in progress'],
    ['Third-party questionnaire submitted by third party', 'Third-party questionnaire in progress'],
    ['Final review submitted', 'Final review in progress'],
];

/**
 * Renders in one of the following ways:
 * * In the case where there is no service for a third party: an empty table cell.
 * * In the case where an assessment has been completed, and the next assessment has not yet begun: text describing the assessment status and whether a follow-up assessment has been scheduled.
 * * In all other cases: text describing the next step in the service assessment process to be completed, a corresponding progress bar, and a tooltip/hover describing the steps represented by the progress bar.
 *
 * TODO [TPRM - managing third parties and services]: Test this component.
 */
export const ServiceAssessmentProgressTableCell = (props: { rowData: ThirdPartiesAndServicesTableRowData }) => {
    const { serviceData, userHasTprmRole } = props.rowData;

    if (!serviceData) {
        return <TableCell className={sharedStyles.parentCell} />;
    } else if (serviceData.service.last_assessed && serviceData.service.assessment_workflow_data.state === ServiceAssessmentState.AWAITING_ASSESSMENT) {
        return <PrimaryTextAndSecondaryTextTableCell primaryText="Assessment completed" secondaryText={serviceData.service.assessment_workflow_setup.due_date ? 'Reassessment scheduled' : 'No reassessment scheduled'} />;
    }

    const [steps, completedStepNumber] = userHasTprmRole ? [STEPS_TPRM_USER, serviceData.serviceCompletedStepNumber] : [STEPS_NON_TPRM_USER, serviceData.serviceCompletedStepNumber];

    const getProgressBarPropsForStep = (step: number) => ({
        now: 100 / steps.length,
        className: step <= completedStepNumber ? styles.progressBarSegmentComplete : styles.progressBarSegmentIncomplete,
    });

    const tooltipContent = (
        <div className={styles.tooltipContent}>
            {steps.map((step, index) => (
                <TooltipRow key={index} step={index} progress={completedStepNumber} text={step[0]} />
            ))}
        </div>
    );

    return (
        <TableCell className={sharedStyles.parentCell}>
            <div className={styles.progressBarAndText}>
                <OverlayTrigger placement="auto" delay={{ show: 100, hide: 200 }} overlay={<Tooltip id="information">{tooltipContent}</Tooltip>}>
                    <ProgressBar className={styles.progressBarContainer}>
                        {steps.map((_, index) => (
                            <ProgressBar key={index} {...getProgressBarPropsForStep(index)} />
                        ))}
                    </ProgressBar>
                </OverlayTrigger>
                <Text noStyles color="darkGray">
                    {steps[completedStepNumber + 1][1]}
                </Text>
            </div>
        </TableCell>
    );
};

const TooltipRow = ({ step, progress, text }: { step: number; progress: number; text: string }) => {
    const isComplete = step <= progress;

    return (
        <div className={styles.tooltipRow}>
            <FontAwesomeIcon title={`${text}: ${isComplete ? 'Complete' : 'Incomplete'}`} icon={isComplete ? faCheck : faX} className={styles.tooltipIcon} />
            <Text noStyles color="white">
                {text}
            </Text>
        </div>
    );
};
